import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { i18n } from './ui/i18n';
import { watermarkCanvas } from './lib/watermark';
import CanvasPreview, { type HoverPreview } from './components/CanvasPreview';
import SeparationsPanel from './components/SeparationsPanel';
import ColorwaysPanel, { type ColorwayEntry } from './components/ColorwaysPanel';
import AutoVariantsPanel from './components/AutoVariantsPanel';
import QualityPanel from './components/QualityPanel';
import RepeatPanel, { type RepeatView } from './components/RepeatPanel';
import RightPanel, { type PanelTab } from './components/RightPanel';
import { useMeasure } from './lib/unitsSettings';
import { formatDim } from '@imagelayerdecomposer/core';
// Type-only import: the actual exporter module is loaded on demand (ag-psd is
// heavy) so the initial bundle stays small.
import type { ExportMeta, FullResData } from './lib/exporters';
import {
  type SeparationOptions,
  type RepeatOptions,
  type PaletteColor,
  applyColorway,
  toUint8Array,
} from '@imagelayerdecomposer/core';

const DEFAULT_REPEAT: RepeatOptions = {
  tileW: 0,
  tileH: 0,
  ratio: 0.5,
  type: 'grid',
  dx: 0,
  dy: 0,
};

const DEFAULT_OPTIONS: SeparationOptions = {
  mode: 'soft',
  nColors: 5,
  nearestK: 3,
  distanceSpace: 'lab',
  regularization: 0,
  noiseReduction: 0,
  speckRemoval: 0,
  halftoneEnabled: false,
  halftoneFrequency: 16,
  halftoneAngle: 0,
  halftoneShape: 'round',
  maxPrintColors: 12,
};

/** Right-panel tab persistence (localStorage may be unavailable). */
const PANEL_TAB_KEY = 'colarsep.panelTab';

function readPanelTab(): PanelTab {
  try {
    const stored = localStorage.getItem(PANEL_TAB_KEY);
    if (stored === 'separations' || stored === 'colorways' || stored === 'repeat') {
      return stored;
    }
  } catch {
    /* private mode / disabled storage: fall through */
  }
  return 'separations';
}

interface ColorwaySnapshot {
  colorways: ColorwayEntry[];
  activeId: string;
}

function cloneColorways(list: ColorwayEntry[]): ColorwayEntry[] {
  return list.map((cw) => ({
    ...cw,
    colors: cw.colors.map((c) => ({ ...c, lab: { ...c.lab } })),
  }));
}

function uniqueId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Small alpha maps (max ~360 px) for the hover preview. Recoloring at this
 * size is interactive; the full working copy is never recomposited on hover.
 */
function buildSmallMaps(
  alphaMaps: { width: number; height: number; maps: { width: number; height: number; data: Float32Array }[] },
  maxDim = 384
): { width: number; height: number; maps: { width: number; height: number; data: Float32Array }[] } | null {
  if (!alphaMaps || !alphaMaps.maps?.length) return null;
  const { width, height, maps } = alphaMaps;
  const stride = Math.max(1, Math.ceil(Math.max(width, height) / maxDim));
  if (stride === 1) return { width, height, maps };
  const w = Math.max(1, Math.floor(width / stride));
  const h = Math.max(1, Math.floor(height / stride));
  const small = maps.map((m) => {
    const data = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      const sy = Math.min(height - 1, y * stride);
      for (let x = 0; x < w; x++) {
        data[y * w + x] = m.data[sy * width + Math.min(width - 1, x * stride)];
      }
    }
    return { width: w, height: h, data };
  });
  return { width: w, height: h, maps: small };
}

export default function App({
  paused = false,
  exportGate,
  banner,
}: {
  paused?: boolean;
  /** licensing gate: enforced BOTH in the UI and inside runExport (#1) */
  exportGate?: { canExport: boolean; watermark: boolean };
  /** licensing banner node placed right under the toolbar */
  banner?: React.ReactNode;
}) {
  // While /account or /pricing is shown, the editor stays mounted (state
  // preserved) but must not react to keyboard shortcuts (plan change #9).
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const gate = exportGate ?? { canExport: true, watermark: false };
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [previewResult, setPreviewResult] = useState<any>(null);
  // Mirror of previewResult for the worker message handler: the handler is
  // attached once, so reading state directly would capture a stale null.
  const previewResultRef = useRef<any>(null);
  previewResultRef.current = previewResult;
  // Only one automatic map re-send per preview, so a persistent worker problem
  // cannot turn into an infinite RECOLOR <-> ERROR loop.
  const mapsResentRef = useRef(false);
  const [previewData, setPreviewData] = useState<Uint8ClampedArray | null>(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const [previewHeight, setPreviewHeight] = useState(0);
  const [previewMasks, setPreviewMasks] = useState<Uint8ClampedArray[]>([]);
  const [visibleMasks, setVisibleMasks] = useState<boolean[]>([]);
  const [coverage, setCoverage] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [exportProgress, setExportProgress] = useState<{ processed: number; total: number } | null>(null);
  const [exportCancelled, setExportCancelled] = useState(false);  const [previewStage, setPreviewStage] = useState<{ stage: string; percent: number } | null>(null);
  const [workerError, setWorkerError] = useState<string | null>(null);
  const [repeat, setRepeat] = useState<RepeatOptions>({ ...DEFAULT_REPEAT });
  const [repeatView, setRepeatView] = useState<RepeatView>({ count: 2, seamCheck: false });
  const [options, setOptions] = useState<SeparationOptions>({ ...DEFAULT_OPTIONS });
  const [panelTab, setPanelTab] = useState<PanelTab>(readPanelTab);
  // Restored panel tab also decides the initial canvas view (Repeat <-> repeat
  // preview, others <-> tile), matching the tab-sync rule.
  const [activeTab, setActiveTab] = useState<'tile' | 'repeat'>(
    panelTab === 'repeat' ? 'repeat' : 'tile'
  );
  const [zoom, setZoom] = useState(100);
  // Display unit (px | mm @ print PPI). Pixels stay the ONLY internal unit;
  // conversions happen just before rendering values and just after user input.
  const measure = useMeasure();
  // ---- Colorways: App owns the list so tabs, undo/redo and the worker stay
  // in sync. The worker keeps the alpha maps; switching colorways only sends
  // the new colors (one-directional recoloring).
  const [colorways, setColorways] = useState<ColorwayEntry[]>([]);
  const [activeColorwayId, setActiveColorwayId] = useState('');
  const [lockedLayerIds, setLockedLayerIds] = useState<string[]>([]);
  const [hoverPreview, setHoverPreview] = useState<HoverPreview | null>(null);
  const smallMapsRef = useRef<ReturnType<typeof buildSmallMaps>>(null);

  // Undo/redo for colorway operations (add / delete / apply / rename / edit).
  const undoRef = useRef<ColorwaySnapshot[]>([]);
  const redoRef = useRef<ColorwaySnapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const workerRef = useRef<Worker | null>(null);

  // synchronous-feel flag while a PSD composite is being parsed/rasterized
  const [fileParsing, setFileParsing] = useState(false);

  // Export job: what the user asked for when the full-res separation started.
  // The worker message handler is registered once, so the request details are
  // captured in refs instead of read from (stale) component state.
  const exportIntentRef = useRef<'psd' | 'masks' | null>(null);
  const exportJobRef = useRef<{ colors: PaletteColor[]; meta: ExportMeta } | null>(null);
  const exportDoneRef = useRef<
    (data: { alphas: Uint8Array[]; width: number; height: number }) => void
  >(() => {});
  const exportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Never allow an endless spinner: if the worker stays silent for 45 s
  // after a PREVIEW/RECOLOR request (dead worker, version mismatch, lost
  // message), surface an error instead of "Processing preview…" forever.
  const silentWorkerTimerRef = useRef<number | null>(null);

  const armSilentWorkerTimer = useCallback(() => {
    if (silentWorkerTimerRef.current !== null) {
      window.clearTimeout(silentWorkerTimerRef.current);
    }
    silentWorkerTimerRef.current = window.setTimeout(() => {
      silentWorkerTimerRef.current = null;
      setIsProcessing(false);
      setPreviewStage(null);
      setExportProgress(null);
      setWorkerError(
        'Worker did not respond within 45 s — stale page or worker crash. Reload the page (Ctrl+Shift+R) and retry.'
      );
    }, 45000);
  }, []);

  const clearSilentWorkerTimer = useCallback(() => {
    if (silentWorkerTimerRef.current !== null) {
      window.clearTimeout(silentWorkerTimerRef.current);
      silentWorkerTimerRef.current = null;
    }
  }, []);

  // Initialize worker
  useEffect(() => {
    const worker = new Worker(new URL('./worker.ts', import.meta.url), {
      // MUST be a module worker: the file uses ESM imports. Without this the
      // browser loads it as a classic worker and throws
      // "Cannot use import statement outside a module".
      type: 'module',
    });
    workerRef.current = worker;

    // A worker that fails to LOAD (module error, network error after a dev
    // server restart) never answers — catch it explicitly.
    const onWorkerFailure = (e: unknown) => {
      console.error('Worker failed:', e);
      const msg = (e as ErrorEvent)?.message;
      setWorkerError(
        `Worker failed to load${msg ? `: ${msg}` : ''} — reload the page (Ctrl+Shift+R).`
      );
      setIsProcessing(false);
      setPreviewStage(null);
      setExportProgress(null);
    };
    worker.onerror = onWorkerFailure;
    worker.onmessageerror = onWorkerFailure;

    return () => {
      worker.onerror = null;
      worker.onmessageerror = null;
      worker.terminate();
    };
  }, []);

  // Listen for worker messages
  useEffect(() => {
    if (!workerRef.current) return;

    const handler = (e: MessageEvent) => {
      const { data } = e;
      if (data.type === 'PREVIEW_RESULT') {
        clearSilentWorkerTimer();
        // A fresh preview means the worker holds current maps again.
        mapsResentRef.current = false;
        setPreviewResult(data.result);
        setPreviewData(data.previewData);
        setPreviewWidth(data.result.alphaMaps.width);
        setPreviewHeight(data.result.alphaMaps.height);
        setPreviewMasks(data.masks);
        // Masks start hidden: the separated composite is the artwork the user
        // wants to see; the eye buttons reveal each color's coverage.
        setVisibleMasks(data.result.alphaMaps.maps.map(() => false));
        setCoverage(data.coverage ?? []);
        setIsProcessing(false);
        setExportProgress(null);
        setExportCancelled(false);
        setPreviewStage(null);
        setWorkerError(null);

        // New separation => new palette => new colorway list and clean history.
        smallMapsRef.current = buildSmallMaps(data.result.alphaMaps);
        setColorways([data.result.colorway]);
        setActiveColorwayId(data.result.colorway.id);
        setLockedLayerIds([]);
        setHoverPreview(null);
        undoRef.current = [];
        redoRef.current = [];
        setCanUndo(false);
        setCanRedo(false);
      } else if (data.type === 'RECOLOR_RESULT') {
        // Only colors changed; alpha maps stay exactly as they were.
        clearSilentWorkerTimer();
        // The worker answered and therefore holds maps again: a later loss
        // (another dev reload) may be recovered once more.
        mapsResentRef.current = false;
        setPreviewData(data.previewData);
        setPreviewResult((prev: any) =>
          prev
            ? {
                ...prev,
                colorway: { ...prev.colorway, colors: data.colors },
              }
            : prev
        );
        setIsProcessing(false);
        setPreviewStage(null);
        setWorkerError(null);
      } else if (data.type === 'PREVIEW_PROGRESS') {
        setPreviewStage({ stage: data.stage, percent: data.percent });
      } else if (data.type === 'EXPORT_PROGRESS') {
        setExportProgress({ processed: data.processed, total: data.total });
      } else if (data.type === 'EXPORT_DONE') {
        setPreviewResult((prev: any) => ({
          ...prev,
          fullResAlphas: data.alphas,
          fullResWidth: data.width,
          fullResHeight: data.height,
        }));
        // Build the requested file(s) (PSD / masks zip) and clear the progress
        // UI only once the download has been handed to the browser.
        setExportCancelled(false);
        setPreviewStage(null);
        exportDoneRef.current({
          alphas: data.alphas,
          width: data.width,
          height: data.height,
        });
      } else if (data.type === 'EXPORT_CANCELLED') {
        setIsProcessing(false);
        setExportProgress(null);
        setExportCancelled(true);
        setPreviewStage(null);
        setWorkerError(null);
      } else if (data.type === 'ERROR') {
        // The worker lost its preview state (e.g. it was recreated by a dev
        // HMR update while the page kept its React state). The UI still holds
        // the alpha maps, so hand them back once and retry the recolor instead
        // of showing a permanent "run preview again" banner.
        if (data.code === 'NO_PREVIEW_DATA') {
          const result = previewResultRef.current;
          if (result && workerRef.current && !mapsResentRef.current) {
            mapsResentRef.current = true;
            console.warn('[app] Worker lost preview maps — re-sending them once.');
            armSilentWorkerTimer();
            workerRef.current.postMessage({
              type: 'RECOLOR',
              colors: result.colorway.colors,
              colorwayName: result.colorway.name,
              alphaMaps: result.alphaMaps,
            });
            return;
          }
        }
        clearSilentWorkerTimer();
        console.error('Worker error:', data.message);
        setIsProcessing(false);
        setExportProgress(null);
        setPreviewStage(null);
        setWorkerError(data.message);
      }
    };

    workerRef.current!.addEventListener('message', handler);
    return () => {
      workerRef.current!.removeEventListener('message', handler);
    };
  }, [clearSilentWorkerTimer, armSilentWorkerTimer]);

  const runPreviewSeparation = useCallback(
    (img: HTMLImageElement, opts: SeparationOptions) => {
      if (!workerRef.current) return;

      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      setIsProcessing(true);
      setWorkerError(null);
      armSilentWorkerTimer();
      workerRef.current!.postMessage(
        { type: 'PREVIEW', imageData, options: opts },
        [imageData.data.buffer]
      );
    },
    [options, armSilentWorkerTimer]
  );

  /**
   * Feeds a raster (or PSD composite rasterized to PNG) through the SAME
   * pipeline as before: image state → repeat defaults → preview separation.
   * One entry point so error handling and revoking can't drift apart.
   */
  const attachRasterImage = useCallback(
    (blob: Blob, sourceName: string) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onerror = () => {
        URL.revokeObjectURL(url);
        setWorkerError(`Image could not be decoded (${sourceName}).`);
      };
      img.onload = () => {
        URL.revokeObjectURL(url);
        setImage(img);
        setRepeat({
          ...DEFAULT_REPEAT,
          tileW: img.width,
          tileH: img.height,
        });
        runPreviewSeparation(img, options);
      };
      img.src = url;
    },
    [options, runPreviewSeparation]
  );

  /**
   * PSD import: uses the FLATTENED (composite) canvas — separating per PSD
   * layer is a separate future stage. The parser receives untrusted bytes, so
   * everything runs inside a catch and canvas size limits are checked BEFORE
   * allocation; every failure ends in a visible message, never silence.
   */
  const loadPsdFile = useCallback(
    async (file: File) => {
      setFileParsing(true);
      try {
        const { readPsd } = await import('ag-psd');
        const buf = await file.arrayBuffer();
        const psd = readPsd(buf, { skipLayerImageData: true, skipThumbnail: true });
        if (!psd.canvas) {
          throw new Error(
            'no composite image data. Save the PSD with Photoshop "Maximize compatibility" ON.'
          );
        }
        // Browser canvas limits (Chromium: side ≤ 16384, area ≤ ~268 MP) —
        // reject early instead of letting the browser fail mid-draw.
        const MAX_SIDE = 16384;
        const MAX_AREA = 268_000_000;
        if (psd.width > MAX_SIDE || psd.height > MAX_SIDE || psd.width * psd.height > MAX_AREA) {
          throw new Error(
            `canvas too large: ${psd.width} × ${psd.height} (limit ${MAX_SIDE} px side / ~268 MP area)`
          );
        }
        const png = await new Promise<Blob | null>((resolve) => {
          psd.canvas!.toBlob(resolve, 'image/png');
        });
        if (!png) throw new Error('composite could not be rasterized');
        attachRasterImage(png, file.name);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setWorkerError(`PSD could not be loaded: ${msg}`);
      } finally {
        setFileParsing(false);
      }
    },
    [attachRasterImage]
  );

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // re-enable picking the SAME file again (change won't fire otherwise)
      e.target.value = '';
      if (/\.psd$/i.test(file.name) || file.type === 'image/vnd.adobe.photoshop') {
        void loadPsdFile(file);
      } else {
        attachRasterImage(file, file.name);
      }
    },
    [loadPsdFile, attachRasterImage]
  );



  const handleOptionsChange = useCallback(
    (newOpts: Partial<SeparationOptions>) => {
      setOptions((prev) => ({ ...prev, ...newOpts }));
      if (image) {
        runPreviewSeparation(image, { ...options, ...newOpts });
      }
    },
    [image, options, runPreviewSeparation]
  );

  // ---- Colorway plumbing -------------------------------------------------

  const activeColorway =
    colorways.find((c) => c.id === activeColorwayId) ?? colorways[0] ?? null;
  const colors: PaletteColor[] = activeColorway?.colors ?? [];

  const snapshot = useCallback(
    (): ColorwaySnapshot => ({
      colorways: cloneColorways(colorways),
      activeId: activeColorwayId,
    }),
    [colorways, activeColorwayId]
  );

  const refreshHistoryFlags = useCallback(() => {
    setCanUndo(undoRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
  }, []);

  const recolorWith = useCallback(
    (nextColors: PaletteColor[], name: string) => {
      if (!workerRef.current || nextColors.length === 0) return;
      setIsProcessing(true);
      setWorkerError(null);
      armSilentWorkerTimer();
      workerRef.current.postMessage({
        type: 'RECOLOR',
        colors: nextColors,
        colorwayName: name,
      });
    },
    [armSilentWorkerTimer]
  );

  /**
   * Single mutation path for the colorway list: records undo history, updates
   * the state and (unless disabled) recolors the canvas. Every colorway
   * add/delete/apply/rename/edit goes through here, which is what puts them
   * into undo/redo.
   */
  const commitColorways = useCallback(
    (
      next: ColorwayEntry[],
      nextActiveId: string,
      opts?: { recolor?: boolean }
    ) => {
      undoRef.current.push(snapshot());
      if (undoRef.current.length > 80) undoRef.current.shift();
      redoRef.current = [];
      refreshHistoryFlags();
      setColorways(next);
      setActiveColorwayId(nextActiveId);
      setHoverPreview(null);
      if (opts?.recolor !== false) {
        const active = next.find((c) => c.id === nextActiveId);
        if (active) recolorWith(active.colors, active.name);
      }
    },
    [snapshot, refreshHistoryFlags, recolorWith]
  );

  const restoreSnapshot = useCallback(
    (snap: ColorwaySnapshot) => {
      setColorways(snap.colorways);
      setActiveColorwayId(snap.activeId);
      setHoverPreview(null);
      const active = snap.colorways.find((c) => c.id === snap.activeId) ?? snap.colorways[0];
      if (active) recolorWith(active.colors, active.name);
    },
    [recolorWith]
  );

  const handleUndo = useCallback(() => {
    const prev = undoRef.current.pop();
    if (!prev) return;
    redoRef.current.push(snapshot());
    refreshHistoryFlags();
    restoreSnapshot(prev);
  }, [snapshot, refreshHistoryFlags, restoreSnapshot]);

  const handleRedo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(snapshot());
    refreshHistoryFlags();
    restoreSnapshot(next);
  }, [snapshot, refreshHistoryFlags, restoreSnapshot]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (pausedRef.current) return; // editor hidden behind /account etc.
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
      } else if (key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo, handleRedo]);

  // Keep previewResult's colorway in sync with the editable list. Export and
  // the worker recovery path both read previewResult.
  useEffect(() => {
    if (!activeColorway) return;
    setPreviewResult((prev: any) =>
      prev
        ? {
            ...prev,
            colorway: {
              ...prev.colorway,
              id: activeColorway.id,
              name: activeColorway.name,
              colors: activeColorway.colors,
            },
          }
        : prev
    );
  }, [activeColorway]);

  /** Edit one layer color of the ACTIVE colorway (undoable). */
  const handleRecolor = useCallback(
    (colorIndex: number, lab: { L: number; a: number; b: number }) => {
      if (!activeColorway) return;
      const nextColors = activeColorway.colors.map((c, i) =>
        i === colorIndex ? { ...c, lab } : c
      );
      commitColorways(
        colorways.map((cw) =>
          cw.id === activeColorway.id ? { ...cw, colors: nextColors } : cw
        ),
        activeColorway.id
      );
    },
    [activeColorway, colorways, commitColorways]
  );

  const handleApplyColorway = useCallback(
    (id: string) => {
      if (id === activeColorwayId) return;
      if (!colorways.some((c) => c.id === id)) return;
      commitColorways(colorways, id);
    },
    [activeColorwayId, colorways, commitColorways]
  );

  const handleDuplicateColorway = useCallback(
    (id: string) => {
      const source = colorways.find((c) => c.id === id) ?? activeColorway;
      if (!source) return;
      const copy: ColorwayEntry = {
        id: uniqueId('cw'),
        name: `${source.name} copy`,
        colors: cloneColorways([source])[0].colors,
      };
      commitColorways([...colorways, copy], copy.id);
    },
    [colorways, activeColorway, commitColorways]
  );

  const handleNewColorway = useCallback(() => {
    handleDuplicateColorway(activeColorwayId);
  }, [handleDuplicateColorway, activeColorwayId]);

  const handleDeleteColorway = useCallback(
    (id: string) => {
      if (colorways.length <= 1) return;
      const next = colorways.filter((c) => c.id !== id);
      const nextActive = id === activeColorwayId ? next[0].id : activeColorwayId;
      commitColorways(next, nextActive);
    },
    [colorways, activeColorwayId, commitColorways]
  );

  const handleRenameColorway = useCallback(
    (id: string, name: string) => {
      commitColorways(
        colorways.map((c) => (c.id === id ? { ...c, name } : c)),
        activeColorwayId,
        { recolor: false }
      );
    },
    [colorways, activeColorwayId, commitColorways]
  );

  /** Add one generated variant as a new colorway and activate it. */
  const handleAddVariantColorway = useCallback(
    (palette: PaletteColor[], name: string) => {
      const cw: ColorwayEntry = {
        id: uniqueId('cw'),
        name,
        colors: palette.map((c) => ({ ...c, lab: { ...c.lab } })),
      };
      commitColorways([...colorways, cw], cw.id);
    },
    [colorways, commitColorways]
  );

  /** Add every generated variant without changing the active colorway. */
  const handleAddAllVariants = useCallback(
    (variants: PaletteColor[][], label: string) => {
      if (variants.length === 0) return;
      const added: ColorwayEntry[] = variants.map((palette, i) => ({
        id: uniqueId('cw'),
        name: `${label} ${i + 1}`,
        colors: palette.map((c) => ({ ...c, lab: { ...c.lab } })),
      }));
      commitColorways([...colorways, ...added], activeColorwayId, { recolor: false });
    },
    [colorways, activeColorwayId, commitColorways]
  );

  /** Hover preview from Auto Variants (low-res working copy composite). */
  const handlePreviewVariant = useCallback((variantColors: PaletteColor[] | null) => {
    if (!variantColors) {
      setHoverPreview(null);
      return;
    }
    const small = smallMapsRef.current;
    if (!small) return;
    const buf = applyColorway(
      { alphaMaps: small } as any,
      { id: 'hover', name: 'hover', colors: variantColors }
    );
    const rgba = toUint8Array({ ...buf, colorSpace: 'linear' } as any);
    setHoverPreview({ data: rgba, width: buf.width, height: buf.height });
  }, []);

  const handleToggleLayerLock = useCallback((id: string) => {
    setLockedLayerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleReorder = useCallback(
    (from: number, to: number) => {
      if (from === 0 || to === 0 || from === to) return; // paper pinned
      const permute = <T,>(arr: T[]): T[] => {
        const next = [...arr];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      };
      // Every colorway stores colors in layer order, so all of them must be
      // permuted together; otherwise switching colorways would mismatch.
      setColorways((prev) =>
        prev.map((cw) => ({ ...cw, colors: permute(cw.colors) }))
      );
      // Undo snapshots also store layer-ordered palettes: apply the same
      // permutation so restoring them later still matches the permuted alpha
      // maps (otherwise undo would paint colors onto the wrong layers).
      const permuteSnapshot = (snap: ColorwaySnapshot): ColorwaySnapshot => ({
        ...snap,
        colorways: snap.colorways.map((cw) => ({ ...cw, colors: permute(cw.colors) })),
      });
      undoRef.current = undoRef.current.map(permuteSnapshot);
      redoRef.current = redoRef.current.map(permuteSnapshot);
      setPreviewResult((prev: any) =>
        prev
          ? {
              ...prev,
              alphaMaps: { ...prev.alphaMaps, maps: permute(prev.alphaMaps.maps) },
            }
          : prev
      );
      setPreviewMasks((prev) => permute(prev));
      setVisibleMasks((prev) => permute(prev));
      setCoverage((prev) => permute(prev));
      if (smallMapsRef.current) {
        smallMapsRef.current = {
          ...smallMapsRef.current,
          maps: permute(smallMapsRef.current.maps),
        };
      }
      workerRef.current?.postMessage({ type: 'REORDER', from, to });
    },
    []
  );

  const handleMaskToggle = useCallback((index: number) => {
    setVisibleMasks((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  const handleRepeatChange = useCallback((newRepeat: Partial<RepeatOptions>) => {
    setRepeat((prev) => ({ ...prev, ...newRepeat }));
  }, []);

  const handleRepeatViewChange = useCallback((patch: Partial<RepeatView>) => {
    setRepeatView((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleResetPan = useCallback(() => {
    setRepeat((prev) => ({ ...prev, dx: 0, dy: 0 }));
  }, []);

  /** Panel tab change also syncs the canvas view (see task spec). */
  const handlePanelTabChange = useCallback((tab: PanelTab) => {
    setPanelTab(tab);
    setHoverPreview(null);
    setActiveTab(tab === 'repeat' ? 'repeat' : 'tile');
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_TAB_KEY, panelTab);
    } catch {
      /* storage unavailable — tabs still work for this session */
    }
  }, [panelTab]);

  /**
   * Full-resolution export. The worker separates the original image tile by
   * tile; when it reports EXPORT_DONE we build the requested artifact from the
   * full-res masks (PSD layers, or a mask PNG zip). `intent` is stored in a ref
   * because the worker message handler is registered once.
   */
  const runExport = useCallback(
    (intent: 'psd' | 'masks') => {
      if (!image || !previewResult || !workerRef.current || !activeColorway) {
        setWorkerError('Export needs an image and a colorway first.');
        return;
      }
      if (isProcessing) return;
      // Licensing gate enforced here (not only in the dropdown UI) so every
      // entry point — dropdown, future shortcuts — is covered (#1).
      if (!gate.canExport) {
        setWorkerError('Export disabled: license expired or inactive. Upgrade to continue.');
        return;
      }

      exportIntentRef.current = intent;
      exportJobRef.current = {
        colors: activeColorway.colors.map((c) => ({ ...c, lab: { ...c.lab } })),
        meta: {
          name: activeColorway.name,
          colorways: cloneColorways(colorways),
          activeColorwayId: activeColorway.id,
          paperColor: previewResult.paperColor ?? null,
          mode: options.mode,
          nearestK: options.nearestK,
        },
      };

      setExportNote(null);
      setIsProcessing(true);
      setExportCancelled(false);
      setWorkerError(null);
      setExportProgress({ processed: 0, total: 1 });

      // If EXPORT_DONE never arrives, stop the spinner after 2 minutes.
      const exportTimeout = setTimeout(() => {
        setIsProcessing(false);
        setExportProgress(null);
        setWorkerError('Export timed out — worker did not respond. Try again.');
      }, 120_000);

      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(image, 0, 0);
        const imageData = ctx.getImageData(0, 0, image.width, image.height);

        workerRef.current.postMessage(
          {
            type: 'EXPORT',
            imageData,
            options,
            palette: activeColorway.colors,
            paperColor: previewResult.paperColor,
          },
          [imageData.data.buffer]
        );
      } catch (err) {
        clearTimeout(exportTimeout);
        setIsProcessing(false);
        setExportProgress(null);
        setWorkerError(`Export failed to start: ${err instanceof Error ? err.message : String(err)}`);
      }

      // Clear the timeout once EXPORT_DONE arrives (handled in exportDoneRef).
      exportTimeoutRef.current = exportTimeout;
    },
    [image, previewResult, options, activeColorway, colorways, isProcessing, gate]
  );

  const cancelExport = useCallback(() => {
    if (exportTimeoutRef.current) {
      clearTimeout(exportTimeoutRef.current);
      exportTimeoutRef.current = null;
    }
    exportIntentRef.current = null;
    exportJobRef.current = null;
    workerRef.current?.postMessage({ type: 'CANCEL_EXPORT' });
    setIsProcessing(false);
    setExportProgress(null);
    setExportCancelled(true);
  }, []);

  // Builds the file(s) for the export that just completed. Assigned every
  // render so it always sees the current colorways / naming.
  exportDoneRef.current = (data) => {
    if (exportTimeoutRef.current) {
      clearTimeout(exportTimeoutRef.current);
      exportTimeoutRef.current = null;
    }
    const job = exportJobRef.current;
    const intent = exportIntentRef.current;
    exportJobRef.current = null;
    exportIntentRef.current = null;

    if (!job || !intent) {
      setIsProcessing(false);
      setExportProgress(null);
      return;
    }

    const full: FullResData = {
      width: data.width,
      height: data.height,
      alphas: data.alphas,
      colors: job.colors,
    };

    void (async () => {
      try {
        // Loaded on demand: ag-psd + jszip only ship when the user exports.
        const exporters = await import('./lib/exporters');
        if (intent === 'psd') {
          const result = await exporters.buildLayeredExport(full, job.meta);
          exporters.downloadBlob(result.blob, result.filename);
          if (result.format === 'zip') {
            setExportNote(
              'PSD was too large for this image — exported mask PNGs as a ZIP instead.'
            );
          }
        } else {
          exporters.downloadBlob(
            await exporters.buildMasksZip(full, job.meta),
            `${exporters.slug(job.meta.name)}.masks.zip`
          );
        }
        setWorkerError(null);
      } catch (err: any) {
        console.error('[export] failed:', err);
        setWorkerError(`Export failed: ${err?.message ?? String(err)}`);
      } finally {
        setIsProcessing(false);
        setExportProgress(null);
      }
    })();
  };

  /** Colorway JSON is pure metadata: no worker round-trip needed. */
  const handleExportColorwayJson = useCallback(() => {
    if (!activeColorway) return;
    const meta: ExportMeta = {
      name: activeColorway.name,
      colorways: cloneColorways(colorways),
      activeColorwayId: activeColorway.id,
      paperColor: previewResult?.paperColor ?? null,
      mode: options.mode,
      nearestK: options.nearestK,
    };
    void (async () => {
      const { colorwayJson, downloadBlob, slug } = await import('./lib/exporters');
      const blob = new Blob([colorwayJson(meta)], { type: 'application/json' });
      downloadBlob(blob, `${slug(meta.name)}.colorways.json`);
    })();
  }, [activeColorway, colorways, previewResult, options]);

  if (!image) {
    return (
      <div className="app">
        <div className="top-bar">
          <input type="file" accept="image/*,.psd" onChange={handleFile} className="btn" />
          <div className="spacer"></div>
          <div className="status">No image loaded</div>
        </div>
        <div className="main-view" style={{ height: 'calc(100vh - 32px)' }}>
          <div className="center-panel">
            <div className="empty-state">
              <div className="drop-zone" onClick={() => {
                const el = document.querySelector('input[type="file"]') as HTMLInputElement | null;
                el?.click();
              }}>
                <p>Open image</p>
              </div>
            </div>
          </div>
          <div className="right-dock">
            <div className="panel">
              <div className="panel-header">Info</div>
              <div className="hint">Upload an image to begin color separation.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="app">
      <div className="top-bar">
        <input type="file" accept="image/*,.psd" onChange={handleFile} className="btn" />
        <button className="btn" onClick={handleUndo} disabled={!canUndo} title="Undo (Ctrl+Z)">
          Undo
        </button>
        <button className="btn" onClick={handleRedo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
          Redo
        </button>
        <div className="separator"></div>
        <div className="dropdown">
          <button className="btn" onClick={(e) => {
            const el = e.currentTarget.parentElement as HTMLElement | null;
            el?.classList.toggle('open');
          }}>
            Export ▾
          </button>
          <div className="dropdown-menu">
            <div className="dropdown-item" onClick={() => {
              if (previewData) {
                const canvas = document.createElement('canvas');
                canvas.width = previewWidth;
                canvas.height = previewHeight;
                const ctx = canvas.getContext('2d')!;
                const imgData = ctx.createImageData(previewWidth, previewHeight);
                imgData.data.set(previewData);
                ctx.putImageData(imgData, 0, 0);
                if (gate.watermark) watermarkCanvas(canvas, 'TRIAL');
                const url = canvas.toDataURL('image/png');
                const a = document.createElement('a');
                a.href = url;
                a.download = 'combined_preview.png';
                a.click();
              }
            }}>
              Combined PNG
            </div>
            <div className="dropdown-item" onClick={() => runExport('masks')}>
              Mask PNGs (ZIP)
            </div>
            <div className="dropdown-item" onClick={handleExportColorwayJson}>
              Colorway JSON
            </div>
            <div className="dropdown-item" onClick={() => runExport('psd')}>
              Layered PSD / ZIP
            </div>
          </div>
        </div>
        {/* no avatar in the toolbar: Account lives in the BOTTOM-LEFT corner */}
        <div className="spacer"></div>
        {workerError && (
          <div className="status error">
            ⚠️ {workerError}
          </div>
        )}
        {!workerError && previewStage && (
          <div className="status">
            {previewStage.stage} {previewStage.percent}%
          </div>
        )}
        {!workerError && !previewStage && (
          <div className="status">
            {formatDim(image.width, image.height, measure.unit, measure.ppi)} &nbsp;|&nbsp;
            Zoom: {zoom}% &nbsp;|&nbsp;
            Mode: {options.mode === 'soft' ? 'Soft' : 'Flat'}
          </div>
        )}
        {isProcessing && exportProgress && (
          <div className="processing-with-progress" style={{ marginLeft: 14 }}>
            <div>Exporting… {exportProgress.processed} / {exportProgress.total} tiles</div>
            <button className="cancel-btn" onClick={cancelExport}>Cancel</button>
          </div>
        )}
        {isProcessing && !exportProgress && (
          <span className="processing">Processing preview…</span>
        )}
        {fileParsing && <span className="processing">Reading PSD…</span>}
        {exportCancelled && <span className="processing">Export cancelled.</span>}
        {exportNote && <span className="status export-note">ℹ️ {exportNote}</span>}
      </div>
      {banner}

      <div className="main-view">
        <div className="center-panel">
          <div className="canvas-tabs">
            <button
              className={`canvas-tab ${activeTab === 'tile' ? 'active' : ''}`}
              onClick={() => setActiveTab('tile')}
            >
              Tile
            </button>
            <button
              className={`canvas-tab ${activeTab === 'repeat' ? 'active' : ''}`}
              onClick={() => setActiveTab('repeat')}
            >
              Repeat preview
            </button>
          </div>
          <div className="canvas-wrap">
            <div className="canvas-inner" style={{ transform: `scale(${zoom / 100})` }}>
              <CanvasPreview
                image={image}
                previewData={previewData}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                hoverPreview={hoverPreview}
                masks={previewMasks}
                maskWidth={previewWidth}
                maskHeight={previewHeight}
                visibleMasks={visibleMasks}
                colors={colors}
                repeat={repeat}
                repeatView={repeatView}
                activeTab={activeTab}
                onRepeatChange={handleRepeatChange}
              />
            </div>
            <div className="canvas-info">
              {previewWidth > 0 && previewHeight > 0
                ? `${formatDim(previewWidth, previewHeight, measure.unit, measure.ppi)} (working copy)`
                : image.width > 0 && image.height > 0
                  ? `${formatDim(image.width, image.height, measure.unit, measure.ppi)} (source)`
                  : ''}
              {hoverPreview && <span className="hover-badge">variant preview</span>}
            </div>
          </div>
          <div className="status-bar">
            <span>Zoom {zoom}%</span>
            <span className="sep">|</span>
            <span>Tile: {formatDim(repeat.tileW, repeat.tileH, measure.unit, measure.ppi)}</span>
            <span className="sep">|</span>
            <span>{formatDim(image.width, image.height, measure.unit, measure.ppi)}</span>
            <span className="sep">|</span>
            {activeColorway ? <span>Colorway: {activeColorway.name}</span> : null}
            <span className="sep">|</span>
            <label className="unit-picker" title="Display unit for sizes and offsets">
              Unit&nbsp;
              <select
                value={measure.unit}
                onChange={(e) => measure.setUnit(e.target.value as 'px' | 'mm')}
              >
                <option value="mm">mm</option>
                <option value="px">px</option>
              </select>
              {measure.unit === 'mm' && (
                <>
                  PPI&nbsp;
                  <input
                    type="number"
                    min={10}
                    max={10000}
                    step={1}
                    value={measure.ppi}
                    onChange={(e) => measure.setPpi(Number(e.target.value))}
                    style={{ width: 56 }}
                  />
                </>
              )}
            </label>
          </div>
        </div>

        <div className="right-dock">
          <RightPanel
            active={panelTab}
            onChange={handlePanelTabChange}
            panels={{
              separations: (
                <>
                  <SeparationsPanel
                    colors={colors}
                    visibleMasks={visibleMasks}
                    masks={previewMasks}
                    maskWidth={previewWidth}
                    maskHeight={previewHeight}
                    coverage={coverage}
                    onToggle={handleMaskToggle}
                    onRecolor={handleRecolor}
                    onReorder={handleReorder}
                    options={options}
                    onOptionsChange={handleOptionsChange}
                  />
                  <QualityPanel
                    options={options}
                    onOptionsChange={handleOptionsChange}
                    warning={previewResult?.warning ?? null}
                  />
                </>
              ),
              colorways: (
                <>
                  <ColorwaysPanel
                    colorways={colorways}
                    activeId={activeColorwayId}
                    onApply={handleApplyColorway}
                    onDuplicate={handleDuplicateColorway}
                    onNew={handleNewColorway}
                    onDelete={handleDeleteColorway}
                    onRename={handleRenameColorway}
                  />
                  <AutoVariantsPanel
                    colors={colors}
                    coverage={coverage}
                    lockedIds={lockedLayerIds}
                    onToggleLock={handleToggleLayerLock}
                    onPreview={handlePreviewVariant}
                    onAddColorway={handleAddVariantColorway}
                    onAddAll={handleAddAllVariants}
                  />
                </>
              ),
              repeat: (
                <RepeatPanel
                  repeat={repeat}
                  onRepeatChange={handleRepeatChange}
                  view={repeatView}
                  onViewChange={handleRepeatViewChange}
                  zoom={zoom}
                  onZoomChange={setZoom}
                  onResetPan={handleResetPan}
                  unit={measure.unit}
                  ppi={measure.ppi}
                />
              ),
            }}
          />
        </div>
      </div>
      <AvatarMenu />
    </div>
    </>
  );
}

/** Bottom-LEFT Account menu (user plan decision): button + small popup
 * with the signed-in user, Account page link and Sign out. */
function AvatarMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;
  return (
    <div className="account-menu">
      <div
        className={`dropdown-item account-menu-user ${open ? 'open' : ''}`}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
      >
        <span className="account-menu-name">{user.displayName ?? user.username}{user.role === 'admin' ? ' · admin' : ''}</span>
        <span className="account-menu-chev">{open ? '▾' : '▴'}</span>
      </div>
      {open && (
        <div className="account-menu-popup" role="menu">
          <div className="dropdown-item" role="menuitem" onClick={() => { setOpen(false); nav('/account'); }}>
            Account
          </div>
          <div className="dropdown-item" role="menuitem" onClick={() => { setOpen(false); alert('Faturalama bu aşamada yöneticinizle iletişimle yapılır.'); }}>
            Faturalama
          </div>
          <div
            className="dropdown-item"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await logout();
              nav('/', { replace: true });
            }}
          >
            Çıkış yap
          </div>
        </div>
      )}
    </div>
  );
}
